'use client';

import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const ease = [0.22, 1, 0.36, 1] as const;

type LoadingSpinnerProps = {
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  labelClassName?: string;
};

const spinnerSizes = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-10 w-10',
};

export function LoadingSpinner({
  label,
  size = 'md',
  className,
  labelClassName,
}: LoadingSpinnerProps) {
  return (
    <span className={cn('inline-flex items-center justify-center gap-2', className)}>
      <Loader2
        aria-hidden="true"
        className={cn('shrink-0 animate-spin text-neon-cyan', spinnerSizes[size])}
      />
      {label && (
        <span className={cn('text-sm font-medium text-muted', labelClassName)}>
          {label}
        </span>
      )}
    </span>
  );
}

type SkeletonBlockProps = {
  className?: string;
};

export function SkeletonBlock({ className }: SkeletonBlockProps) {
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease }}
      className={cn(
        'relative overflow-hidden rounded-xl border border-white/10 bg-panel/70 shadow-sm',
        'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.7s_infinite]',
        'before:bg-gradient-to-r before:from-transparent before:via-neon-cyan/14 before:to-transparent',
        className
      )}
    />
  );
}

type SkeletonListProps = {
  rows?: number;
  className?: string;
  itemClassName?: string;
};

export function SkeletonList({ rows = 3, className, itemClassName }: SkeletonListProps) {
  return (
    <div className={cn('space-y-3', className)} aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <SkeletonBlock
          key={index}
          className={cn('h-24', itemClassName)}
        />
      ))}
    </div>
  );
}

type SkeletonGridProps = {
  count?: number;
  className?: string;
  itemClassName?: string;
};

export function SkeletonGrid({ count = 4, className, itemClassName }: SkeletonGridProps) {
  return (
    <div
      className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonBlock
          key={index}
          className={cn('h-32', itemClassName)}
        />
      ))}
    </div>
  );
}

type FullSectionLoadingProps = {
  label?: string;
  description?: string;
  className?: string;
};

export function FullSectionLoading({
  label = 'Loading...',
  description,
  className,
}: FullSectionLoadingProps) {
  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease }}
      className={cn(
        'flex min-h-40 items-center justify-center rounded-2xl border border-neon-cyan/20 bg-panel/70 p-8 text-center shadow-[0_0_32px_rgba(0,243,255,0.08)] backdrop-blur',
        className
      )}
    >
      <div className="space-y-3">
        <LoadingSpinner size="lg" className="w-full" />
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          {description && (
            <p className="mt-1 text-xs text-muted">{description}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

type LoadingStateProps =
  | ({ variant?: 'inline' } & LoadingSpinnerProps)
  | ({ variant: 'section' } & FullSectionLoadingProps)
  | ({ variant: 'skeleton' } & SkeletonBlockProps)
  | ({ variant: 'list' } & SkeletonListProps)
  | ({ variant: 'grid' } & SkeletonGridProps);

export default function LoadingState(props: LoadingStateProps) {
  if (props.variant === 'section') {
    return <FullSectionLoading {...props} />;
  }

  if (props.variant === 'skeleton') {
    return <SkeletonBlock {...props} />;
  }

  if (props.variant === 'list') {
    return <SkeletonList {...props} />;
  }

  if (props.variant === 'grid') {
    return <SkeletonGrid {...props} />;
  }

  return <LoadingSpinner {...props} />;
}
